import { useCallback, useMemo, useReducer } from 'react';
import type { FormSegment } from '../../../../../../types.ts';
import type { UseSegmentSelectionReturn, UseSegmentsSelection } from '../types.ts';
import { ActionType, getSegmentBucket, segmentReducer } from '../utils/segmentReducer.ts';

export const useSegmentSelection: UseSegmentsSelection<UseSegmentSelectionReturn> = ({
  directSegments = [],
  rtgSegments = [],
  thirdPartySegments = [],
  siteCategorySegments = [],
  oxPurchaseSegments = [],
}) => {
  const [state, dispatch] = useReducer(segmentReducer, {
    directSegments,
    rtgSegments,
    thirdPartySegments,
    siteCategorySegments,
    oxPurchaseSegments,
  });

  const handleSegmentSelection = useCallback((segment: FormSegment) => {
    const bucket = getSegmentBucket(segment);
    dispatch({ type: ActionType.AddSegment, bucket, segment });
  }, []);

  const handleSegmentRemoval = useCallback((segment: FormSegment) => {
    // Use standardized categorization
    const bucket = getSegmentBucket(segment);
    dispatch({ type: ActionType.RemoveSegment, bucket, id: segment.id });
  }, []);

  const handleRemoveAll = useCallback(() => {
    dispatch({ type: ActionType.RemoveAll });
  }, []);

  const allSelectedSegments = useMemo(() => {
    return [
      ...state.directSegments,
      ...state.rtgSegments,
      ...state.thirdPartySegments,
      ...state.siteCategorySegments,
      ...state.oxPurchaseSegments,
    ];
  }, [
    state.directSegments,
    state.rtgSegments,
    state.thirdPartySegments,
    state.siteCategorySegments,
    state.oxPurchaseSegments,
  ]);

  const hasSelectedSegments = allSelectedSegments.length > 0;
  const selectedCount = allSelectedSegments.length;

  return {
    allSelectedSegments,
    hasSelectedSegments,
    selectedCount,
    handleSegmentSelection,
    handleSegmentRemoval,
    handleRemoveAll,
    directSegments: state.directSegments,
    rtgSegments: state.rtgSegments,
    thirdPartySegments: state.thirdPartySegments,
    siteCategorySegments: state.siteCategorySegments,
    oxPurchaseSegments: state.oxPurchaseSegments,
  };
};
